import { useTranslation } from "react-i18next";

import { cn } from "./ui/utils";

/** 展示用：后端 `chainLogoUrl` 非空才渲染远程图，否则用骨架占位。 */
export function hasValidChainLogoUrl(url: string | undefined | null): boolean {
  return typeof url === "string" && url.trim().length > 0;
}

/**
 * 公共「网络信息卡片」：用于充值 / 提现等弹窗中展示当前链 Logo、网络名称与提示语。
 *
 * 视觉以 Dashboard 提现弹窗为基准（圆形 Logo + ring）。文案复用现有 i18n key：
 *  - dashboard.rechargeModal.networkLabel: "Network: {{name}}"
 *  - dashboard.rechargeModal.networkHint:  "{{name}} network will be used for this transaction"
 */
export type NetworkInfoCardProps = {
  /** 链名称，用于 Logo alt、网络标签与提示文案中的 {{name}} 插值。 */
  name: string;
  /** 链 Logo 图片 URL（来自后端；空则仅 Logo 位显示骨架）。 */
  logoUrl?: string | null;
  /** 可选的外层容器附加 className，便于在不同弹窗中微调间距等。 */
  className?: string;
};

const logoRingClass =
  "h-5 w-5 shrink-0 rounded-full object-cover ring-1 ring-slate-200/80 dark:ring-slate-600/80";

export function NetworkInfoCard({ name, logoUrl, className }: NetworkInfoCardProps) {
  const { t } = useTranslation();
  const showRemoteLogo = hasValidChainLogoUrl(logoUrl);
  return (
    <div
      className={cn(
        "bg-white dark:bg-[#0E0F11] rounded-xl p-4 border border-slate-200 dark:border-[#D2F0E1]/5",
        className
      )}
    >
      <div className="flex items-center gap-3 mb-2">
        {showRemoteLogo ? (
          <img
            src={logoUrl!.trim()}
            alt={name}
            className={logoRingClass}
          />
        ) : (
          <div
            className={cn(logoRingClass, "bg-slate-200 dark:bg-slate-700 animate-pulse")}
            aria-hidden
          />
        )}
        <span className="text-sm font-bold text-slate-900 dark:text-slate-200">
          {t("dashboard.rechargeModal.networkLabel", { name })}
        </span>
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400">
        {t("dashboard.rechargeModal.networkHint", { name })}
      </p>
    </div>
  );
}

export function NetworkInfoCardSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "bg-white dark:bg-[#0E0F11] rounded-xl p-4 border border-slate-200 dark:border-[#D2F0E1]/5",
        className
      )}
      aria-busy="true"
    >
      <div className="flex items-center gap-3 mb-2">
        <div className="h-5 w-5 shrink-0 rounded-full bg-slate-200 dark:bg-slate-700 animate-pulse" />
        <div className="h-4 w-32 rounded bg-slate-200 dark:bg-slate-700 animate-pulse" />
      </div>
      <div className="h-3 w-56 max-w-full rounded bg-slate-200 dark:bg-slate-700 animate-pulse" />
    </div>
  );
}
